import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { ResellerResponseTypes } from "../common/response.common";
import type { CompleteHook, CompleteParam, SelectHook, SelectParam } from "../common/types.common";
import {
    asRemoveHook,
    asUpdateHook,
    buildCompleteParam,
    buildSelectParam,
    get,
    post,
    postEmpty,
} from "./common";

export const reseller = {
    record(data: typeof Requests.Reseller.Record.Input) {
        return post<typeof data, ResellerResponseTypes.Record>(`/reseller/record`, data);
    },
    create(data: typeof Requests.Reseller.Create.Input) {
        return post<typeof data>(`/reseller/create`, data);
    },
    obtain(id: string) {
        return get<ResellerResponseTypes.Obtain>(`/reseller/obtain/${id}`);
    },
    coords(data: typeof Requests.Reseller.Coords.Input) {
        return post<typeof data, ResellerResponseTypes.Coords>(`/reseller/coords`, data);
    },
    delete(id: string) {
        return postEmpty(`/reseller/delete/${id}`);
    },
    insertProduct(id: string, data: typeof Requests.Reseller.UpdateProduct.Input) {
        return post<typeof data, ResellerResponseTypes.InsertProduct>(
            `/reseller/insert-product/${id}`,
            data,
        );
    },
    removeProduct(id: string, data: typeof Requests.Reseller.UpdateProduct.Input) {
        return post<typeof data>(`/reseller/remove-product/${id}`, data);
    },
    resetUserPin(id: string) {
        return postEmpty(`/reseller/pin/reset/${id}`);
    },
    formHooks: {
        select: (async (param?: SelectParam) => {
            const req = get<ResellerResponseTypes.Select>(
                `/reseller/select` + buildSelectParam(param),
            );

            return (await req.execute()) ?? [];
        }) satisfies SelectHook,
        completeArea: (async (param?: CompleteParam) => {
            const req = get<ResellerResponseTypes.CompleteArea>(
                `/reseller/complete-area` + buildCompleteParam(param),
            );

            return (await req.execute()) ?? [];
        }) satisfies CompleteHook,
        update(id: string | undefined, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Reseller.Update.Input) => {
                if (id === undefined) return false;
                return await asUpdateHook(
                    post<typeof data>(`/reseller/update/${id}`, data),
                    isLoading,
                );
            };
        },
        remove(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Reseller.Remove.Input) => {
                return await asRemoveHook(
                    post<typeof data>(`/reseller/remove/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
